// EXTERNAL DEPENDENCIES
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

// HELPERS
import { useImpressionTracking } from '@/hooks'
import { updateEditedProfileDataValues } from '@/redux/features'
import {
  useBusinessProfilePageContent,
  useBusinessProfileReduxState,
  useErrorResetting,
  useModularSellerViewPageContent,
} from '../../hooks'

// COMPONENTS
import { Row } from '@paypalcorp/pp-react'
import ExitConfirmationBox from '@/common/components/ui/confirmation-box'
import {
  BusinessDescriptionSection,
  PageHeaderSection,
  PreviewProfileButton,
  SaveEditsButton,
} from '../'

// CONSTANTS
import { EDIT_DESCRIPTION_PAGE_COMPONENT_TEST_ID } from '../../constants/test-ids'
import { commonModularProfileFptiProperties } from '@/constants'

// FPTI
import {
  EDIT_DESCRIPTION_BACK_LINK_CLICK_FPTI,
  EDIT_DESCRIPTION_PAGE_SHOWN_FPTI,
  EDIT_DESCRIPTION_PREVIEW_BUTTON_CLICK_FPTI,
} from './fpti'
import {
  EDIT_PROFILE_DESCRIPTION_SAVE_BUTTON_CLICK_FPTI,
  EDIT_PROFILE_DESCRIPTION_SUCCESSFUL_SAVE_FPTI,
} from '../owner-profile-page/constants/fpti'

// STYLES
import styles from './styles.module.less'

// TYPES
import { FormattedEditedProfileData } from '../../types/state'

/**
 * Allows the profile owner to edit their business description
 */
export function EditDescriptionPage({
  goBackToMainPage,
}: {
  goBackToMainPage: () => void
}) {
  const isEditedDataInitialized = useRef(false)
  const dispatch = useDispatch()
  useImpressionTracking(EDIT_DESCRIPTION_PAGE_SHOWN_FPTI)

  // INITIALIZE CONTENT
  const getContent = useBusinessProfilePageContent()
  const getModularSellerViewPageContent = useModularSellerViewPageContent()
  const profileDescriptionUpdatedToastMessage = getModularSellerViewPageContent(
    'profileDescriptionUpdatedToast',
  )

  // STATE
  const {
    uneditedFormattedData,
    editedProfileData: { values },
  } = useBusinessProfileReduxState()
  const [shouldShowExitConfirmation, setShouldShowExitConfirmation] =
    useState(false)
  const [isDescriptionValid, setIsDescriptionValid] = useState(true)

  const { businessDescription: originalBusinessDescription } =
    uneditedFormattedData || {}
  const { businessDescription: updatedBusinessDescription } =
    (values as FormattedEditedProfileData) || {}

  const handleBackClick = () => {
    const changesExist =
      originalBusinessDescription !== updatedBusinessDescription

    if (changesExist) {
      setShouldShowExitConfirmation(true)
    } else {
      goBackToMainPage()
    }
  }

  // EFFECTS START
  useEffect(() => {
    // initialize starting description
    if (
      originalBusinessDescription &&
      !isEditedDataInitialized.current &&
      updatedBusinessDescription === ''
    ) {
      isEditedDataInitialized.current = true

      dispatch(
        updateEditedProfileDataValues({
          businessDescription: originalBusinessDescription,
        }),
      )
    }
  }, [dispatch, originalBusinessDescription, updatedBusinessDescription])

  useErrorResetting(isDescriptionValid)
  // EFFECTS END

  return (
    <div
      className={styles.editBusinessDescriptionContainer}
      data-testid={EDIT_DESCRIPTION_PAGE_COMPONENT_TEST_ID}
    >
      <PageHeaderSection
        handleBackClick={handleBackClick}
        pageHeaderText={getContent('editDescriptionHeader')}
        subHeaderText={getContent('editDescriptionSubheader')}
        backLinkFpti={EDIT_DESCRIPTION_BACK_LINK_CLICK_FPTI}
      />
      <div className={styles.sectionContainer}>
        <BusinessDescriptionSection
          isLoading={!uneditedFormattedData.businessHandle}
          setIsValid={setIsDescriptionValid}
        />
        <Row noGutters align='center' className={styles.ctaButtonContainer}>
          <PreviewProfileButton
            canPreview={isDescriptionValid}
            fpti={EDIT_DESCRIPTION_PREVIEW_BUTTON_CLICK_FPTI}
          />
          <SaveEditsButton
            canSave={isDescriptionValid}
            successfulUpdateToastMessage={profileDescriptionUpdatedToastMessage}
            saveButtonFpti={EDIT_PROFILE_DESCRIPTION_SAVE_BUTTON_CLICK_FPTI}
            successPublishFpti={EDIT_PROFILE_DESCRIPTION_SUCCESSFUL_SAVE_FPTI}
            onSuccess={goBackToMainPage}
          />
        </Row>
      </div>
      <ExitConfirmationBox
        isOpen={shouldShowExitConfirmation}
        closeConfirmationDialog={() => {
          setShouldShowExitConfirmation(false)
        }}
        handleBack={goBackToMainPage}
        fpti={{
          source: 'biz_description_page',
          ...commonModularProfileFptiProperties,
        }}
      />
    </div>
  )
}
